/**
 * Query contract for `GET /api/workspace/items`: one definition shared by the
 * backend route validation and the frontend URL parser.
 */
import { z } from 'zod';
import { BULK_DELETE_MAX_IDS } from '../constants/bulk';
import { FILE_KIND_FILTER_IDS } from '../constants/fileKinds';
/**
 * Longest cursor the route accepts. A cursor carries only the last row's id
 * and its binding (workspace, user, params hash), so a real one is small and
 * fixed in size (under 512 characters); the cap only bounds garbage input.
 */
export const WORKSPACE_ITEMS_CURSOR_MAX = 2048;
export const WORKSPACE_ITEMS_SORTS = ['name', 'modified', 'created', 'kind'];
/**
 * A list-valued query param. CSV (`?kinds=a,b`) is the canonical form, and
 * the one `toWorkspaceItemsSearchParams` writes. A repeated param
 * (`?kinds=a&kinds=b`) also works up to 20 repeats: past that Express's qs
 * (`arrayLimit`) yields an index-keyed object, which is rejected as a 400.
 * Values are trimmed and de-duplicated BEFORE the `max` count applies; an
 * empty list reads as absent. A comma is always a separator, so no value can
 * contain one.
 */
const csvOf = (item, max) => z.preprocess((raw) => {
    if (typeof raw !== 'string' && !Array.isArray(raw))
        return raw;
    const parts = (Array.isArray(raw) ? raw : [raw])
        .flatMap((v) => (typeof v === 'string' ? v.split(',') : [v]))
        .map((v) => (typeof v === 'string' ? v.trim() : v))
        .filter((v) => v !== '');
    const unique = [...new Set(parts)];
    return unique.length > 0 ? unique : undefined;
}, z.array(item).max(max).optional());
/**
 * The unrefined query object. Exported because `.refine` makes
 * `.omit/.pick/.partial` throw, and the frontend URL parser validates fields
 * one by one. Route validation uses `workspaceItemsQuerySchema`.
 */
export const workspaceItemsQueryShape = z.object({
    parent: z.union([z.literal('workspace'), z.guid()]).default('workspace'),
    scope: z.enum(['children', 'descendants']).default('children'),
    kinds: csvOf(z.enum(FILE_KIND_FILTER_IDS), 20),
    createdBy: csvOf(z.guid(), 50),
    tags: csvOf(z.string().min(1).max(64), 50),
    modifiedFrom: z.iso.datetime({ offset: true }).optional(),
    modifiedTo: z.iso.datetime({ offset: true }).optional(),
    sort: z.enum(WORKSPACE_ITEMS_SORTS).default('name'),
    order: z.enum(['asc', 'desc']).default('asc'),
    /**
     * Where folders sit. `first`: every folder before every file, each half in
     * sort order (the Folder and Card views draw them as their own section).
     * `mixed`: one stream in sort order, so "newest first" means the newest
     * thing of either kind. Date sorts only: a folder has no Type, and a name
     * sort conventionally keeps folders first.
     */
    folders: z.enum(['first', 'mixed']).default('first'),
    cursor: z.string().max(WORKSPACE_ITEMS_CURSOR_MAX).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(60),
});
/** The sorts `folders: 'mixed'` accepts. */
export const MIXED_FOLDER_SORTS = ['modified', 'created'];
export const workspaceItemsQuerySchema = workspaceItemsQueryShape
    .refine((q) => {
    // Compared as instants (offsets differ). Only when BOTH bounds parse: an
    // invalid bound already has its own error and must not add a second one.
    const from = q.modifiedFrom ? Date.parse(q.modifiedFrom) : NaN;
    const to = q.modifiedTo ? Date.parse(q.modifiedTo) : NaN;
    return Number.isNaN(from) || Number.isNaN(to) || from <= to;
}, { message: 'modifiedFrom must not be after modifiedTo', path: ['modifiedFrom'] })
    .refine((q) => q.folders === 'first' || MIXED_FOLDER_SORTS.includes(q.sort), {
    message: 'folders=mixed needs a date sort (modified or created)',
    path: ['folders'],
});
const WORKSPACE_ITEMS_DEFAULTS = workspaceItemsQueryShape.parse({});
/**
 * Serialize a parsed query back to URL params: CSV for lists, defaults and
 * absent fields omitted. `parse(serialize(q))` equals `q`. Throws on a list
 * value containing a comma, which would otherwise come back as two values.
 */
export function toWorkspaceItemsSearchParams(query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === WORKSPACE_ITEMS_DEFAULTS[key])
            continue;
        if (Array.isArray(value)) {
            if (value.some((item) => String(item).includes(','))) {
                throw new RangeError(`${key} values cannot contain a comma (the list separator)`);
            }
            if (value.length > 0)
                params.set(key, value.join(','));
            continue;
        }
        params.set(key, String(value));
    }
    return params;
}
/** Most ids one thumbnails request signs: one full list page. */
export const WORKSPACE_ITEM_THUMBNAILS_MAX = 100;
/** Query contract for `GET /api/workspace/items/thumbnails` (`?ids=a,b`). */
export const workspaceItemThumbnailsQuerySchema = z.object({
    // Required: `csvOf` reads an empty list as absent, and a refine on an
    // optional value never runs for `undefined`, so the pipe is what rejects it.
    ids: csvOf(z.guid(), WORKSPACE_ITEM_THUMBNAILS_MAX).pipe(z.array(z.guid(), { error: 'Name at least one id' })),
});
/**
 * Body of `POST /api/workspace/items/bulk-move`: folders (`project`) and files
 * (`artifact`) filed into one folder, or into the workspace root (`workspace`,
 * whose id is the workspace id). Capped at `BULK_DELETE_MAX_IDS` as sent;
 * repeats are then dropped, first occurrence kept, so each item appears once
 * in the per-item results.
 */
export const bulkMoveRequestSchema = z.object({
    items: z
        .array(z.object({ type: z.enum(['artifact', 'project']), id: z.guid() }))
        .min(1)
        .max(BULK_DELETE_MAX_IDS)
        .transform((items) => {
        const seen = new Set();
        return items.filter((item) => {
            const key = `${item.type}:${item.id}`;
            if (seen.has(key))
                return false;
            seen.add(key);
            return true;
        });
    }),
    target: z.object({ type: z.enum(['workspace', 'project']), id: z.guid() }),
});
//# sourceMappingURL=workspaceItems.schema.js.map